/**
 * Storefront API Client for LGN
 * Handles GraphQL queries for variant-based collection display
 */

class StorefrontAPI {
  constructor(config = window.LGN_STOREFRONT_CONFIG) {
    if (!config || !config.shopDomain || !config.storefrontAccessToken) {
      console.error('[StorefrontAPI] Missing configuration. Ensure storefront-config snippet is loaded.');
      return;
    }

    this.shopDomain = config.shopDomain;
    this.accessToken = config.storefrontAccessToken;
    this.apiVersion = config.apiVersion || '2025-01';
    this.endpoint = `https://${this.shopDomain}/api/${this.apiVersion}/graphql.json`;
  }

  /**
   * Execute a GraphQL query
   */
  async query(queryString, variables = {}) {
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({
          query: queryString,
          variables
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.errors) {
        console.error('[StorefrontAPI] GraphQL errors:', result.errors);
        throw new Error(result.errors.map(e => e.message).join(', '));
      }

      return result.data;
    } catch (error) {
      console.error('[StorefrontAPI] Query failed:', error);
      throw error;
    }
  }

  /**
   * Fetch all products with variants (no collection filter)
   * Used for /collections/all route
   * Uses search with wildcard to support filters
   */
  async fetchAllProducts(options = {}) {
    // Use search with wildcard to get all products
    // Search API supports productFilters, unlike products query
    return this.searchVariants('*', {
      ...options,
      sortKey: this.mapProductSortToSearchSort(options.sortKey || 'TITLE')
    });
  }

  /**
   * Map ProductSortKeys to SearchSortKeys
   * Products and Search APIs use different enum values
   */
  mapProductSortToSearchSort(productSortKey) {
    const mapping = {
      'TITLE': 'RELEVANCE',           // Default to relevance for search
      'PRICE': 'PRICE',                // Same
      'CREATED_AT': 'RELEVANCE',       // No direct equivalent
      'BEST_SELLING': 'RELEVANCE',     // No direct equivalent
      'MANUAL': 'RELEVANCE',           // No direct equivalent
      'COLLECTION_DEFAULT': 'RELEVANCE' // No direct equivalent
    };

    return mapping[productSortKey] || 'RELEVANCE';
  }

  /**
   * Fetch products with variants from a collection
   * Returns flattened variant array with parent product data
   */
  async fetchCollectionVariants(collectionHandle, options = {}) {
    // Handle special "all" collection by fetching all products
    if (collectionHandle === 'all') {
      return this.fetchAllProducts(options);
    }

    const {
      filters = [],
      first = 50,
      after = null,
      sortKey = 'COLLECTION_DEFAULT',
      reverse = false
    } = options;

    const queryString = `
      query CollectionVariants(
        $handle: String!,
        $first: Int!,
        $after: String,
        $filters: [ProductFilter!],
        $sortKey: ProductCollectionSortKeys,
        $reverse: Boolean
      ) {
        collection(handle: $handle) {
          id
          title
          handle
          products(
            first: $first,
            after: $after,
            filters: $filters,
            sortKey: $sortKey,
            reverse: $reverse
          ) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                title
                handle
                vendor
                productType
                featuredImage {
                  url
                  altText
                }
                variants(first: 100) {
                  edges {
                    node {
                      id
                      title
                      sku
                      availableForSale
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                      selectedOptions {
                        name
                        value
                      }
                      image {
                        url
                        altText
                      }
                      metafields(identifiers: [
                        {namespace: "custom", key: "contenance"},
                        {namespace: "custom", key: "conditionnement"},
                        {namespace: "custom", key: "sur_demande"},
                        {namespace: "custom", key: "visible_in_collection"}
                      ]) {
                        namespace
                        key
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      handle: collectionHandle,
      first,
      after,
      filters: filters.length > 0 ? filters : null,
      sortKey,
      reverse
    };

    const data = await this.query(queryString, variables);

    if (!data.collection) {
      return {
        variants: [],
        pageInfo: { hasNextPage: false, endCursor: null },
        collectionTitle: null
      };
    }

    // Flatten variants with parent product data
    const variants = this.flattenVariants(data.collection.products.edges, filters);

    return {
      variants,
      pageInfo: data.collection.products.pageInfo,
      collectionTitle: data.collection.title,
      collectionHandle: data.collection.handle
    };
  }

  /**
   * Fetch products with variants from search results
   */
  async searchVariants(searchQuery, options = {}) {
    const {
      first = 50,
      after = null,
      filters = [],
      sortKey = 'RELEVANCE',
      reverse = false
    } = options;

    const queryString = `
      query SearchVariants(
        $query: String!,
        $first: Int!,
        $after: String,
        $productFilters: [ProductFilter!],
        $sortKey: SearchSortKeys,
        $reverse: Boolean
      ) {
        search(
          query: $query,
          first: $first,
          after: $after,
          types: PRODUCT,
          productFilters: $productFilters,
          sortKey: $sortKey,
          reverse: $reverse
        ) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ... on Product {
                id
                title
                handle
                vendor
                productType
                featuredImage {
                  url
                  altText
                }
                variants(first: 100) {
                  edges {
                    node {
                      id
                      title
                      sku
                      availableForSale
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                      selectedOptions {
                        name
                        value
                      }
                      image {
                        url
                        altText
                      }
                      metafields(identifiers: [
                        {namespace: "custom", key: "contenance"},
                        {namespace: "custom", key: "conditionnement"},
                        {namespace: "custom", key: "sur_demande"},
                        {namespace: "custom", key: "visible_in_collection"}
                      ]) {
                        namespace
                        key
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      query: searchQuery,
      first,
      after,
      productFilters: filters.length > 0 ? filters : null,
      sortKey,
      reverse
    };

    const data = await this.query(queryString, variables);

    if (!data.search) {
      return {
        variants: [],
        pageInfo: { hasNextPage: false, endCursor: null },
        totalCount: 0
      };
    }

    // Filter out non-product results and flatten variants
    const productEdges = data.search.edges.filter(edge => edge.node.id);
    const variants = this.flattenVariants(productEdges, filters);

    return {
      variants,
      pageInfo: data.search.pageInfo,
      totalCount: data.search.totalCount
    };
  }

  /**
   * Fetch all available filter values for a collection
   * Returns filter facets with counts (for rendering complete filter UI)
   *
   * @param {string} collectionHandle - Collection handle (e.g., 'all')
   * @param {object} options - Query options
   * @param {Array} options.appliedFilters - Currently active filters (for updated counts)
   * @returns {Promise<Object>} Filter groups with values and counts
   */
  async fetchCollectionFilters(collectionHandle, options = {}) {
    const { appliedFilters = [] } = options;

    // Handle special "all" collection
    const queryType = collectionHandle === 'all' ? 'search' : 'collection';

    let queryString;

    if (queryType === 'search') {
      // For /collections/all, use search with wildcard
      queryString = `
        query CollectionFilters($productFilters: [ProductFilter!]) {
          search(query: "*", types: PRODUCT, first: 1, productFilters: $productFilters) {
            productFilters {
              id
              label
              type
              values {
                id
                label
                count
                input
              }
            }
          }
        }
      `;
    } else {
      // For named collections, use collection query
      queryString = `
        query CollectionFilters($handle: String!, $filters: [ProductFilter!]) {
          collection(handle: $handle) {
            products(first: 1, filters: $filters) {
              filters {
                id
                label
                type
                values {
                  id
                  label
                  count
                  input
                }
              }
            }
          }
        }
      `;
    }

    const variables = queryType === 'search'
      ? { productFilters: appliedFilters.length > 0 ? appliedFilters : null }
      : { handle: collectionHandle, filters: appliedFilters.length > 0 ? appliedFilters : null };

    try {
      const data = await this.query(queryString, variables);

      const filters = queryType === 'search'
        ? data.search?.productFilters || []
        : data.collection?.products?.filters || [];

      return {
        filters,
        collectionHandle
      };
    } catch (error) {
      console.error('[StorefrontAPI] Filter fetch error:', error);
      return { filters: [], collectionHandle };
    }
  }

  /**
   * Flatten product edges into variant array with parent product data
   * Optionally filters variants by active variant option filters
   *
   * Filter Logic:
   * - Within same filter group (e.g., multiple sizes): OR (match any value)
   * - Across different filter groups: AND (must match at least one from each group)
   *
   * Example: Vendor: [A, B] AND Size: [75cl, 3L]
   * Shows variants from (Vendor A OR B) that have (Size 75cl OR 3L)
   */
  flattenVariants(productEdges, filters = []) {
    const variants = [];

    // Extract variant option filters for client-side filtering
    const variantOptionFilters = filters.filter(f => f.variantOption);
    // Extract variant metafield filters for client-side filtering
    const variantMetafieldFilters = filters.filter(f => f.variantMetafield);

    for (const productEdge of productEdges) {
      const product = productEdge.node;

      for (const variantEdge of product.variants.edges) {
        const variant = variantEdge.node;

        // Extract size and packaging from selectedOptions
        const options = {};
        for (const opt of variant.selectedOptions) {
          options[opt.name.toLowerCase()] = opt.value;
        }

        // Extract variant metafields into key-value map
        const metafields = {};
        for (const mf of variant.metafields || []) {
          if (mf && mf.key) {  // Skip null metafields
            metafields[mf.key] = mf.value;
          }
        }

        // Client-side filtering: OR within groups, AND across groups
        if (variantOptionFilters.length > 0) {
          // Group filters by option name
          // Example: [{Format: "75cl"}, {Format: "3L"}] → {Format: ["75cl", "3L"]}
          const filterGroups = {};

          for (const filter of variantOptionFilters) {
            const optionName = filter.variantOption.name;
            const optionValue = filter.variantOption.value;

            if (!filterGroups[optionName]) {
              filterGroups[optionName] = [];
            }
            filterGroups[optionName].push(optionValue);
          }

          // Check if variant matches: must match at least one value from EACH group
          let matchesAllGroups = true;

          for (const [optionName, allowedValues] of Object.entries(filterGroups)) {
            // Find variant's value for this option
            const variantOption = variant.selectedOptions.find(
              opt => opt.name === optionName
            );

            // Check if variant's value matches ANY allowed value (OR within group)
            const matchesThisGroup = variantOption && allowedValues.includes(variantOption.value);

            if (!matchesThisGroup) {
              matchesAllGroups = false;
              break;  // Fail fast - variant doesn't match this group
            }
          }

          // Skip variant only if it fails to match ALL filter groups (AND logic)
          if (!matchesAllGroups) {
            continue;
          }
        }

        // Client-side filtering by variant metafields: OR within groups, AND across groups
        if (variantMetafieldFilters.length > 0) {
          // Group filters by metafield key
          // Example: [{contenance: "Magnum"}, {contenance: "75cl"}] → {contenance: ["Magnum", "75cl"]}
          const metafieldFilterGroups = {};

          for (const filter of variantMetafieldFilters) {
            const key = filter.variantMetafield.key;
            const value = filter.variantMetafield.value;

            if (!metafieldFilterGroups[key]) {
              metafieldFilterGroups[key] = [];
            }
            metafieldFilterGroups[key].push(value);
          }

          // Map filter labels back to raw metafield values
          // Search & Discovery sends labels like "3 bouteilles" but metafields store "CBO3"
          const labelToValueMap = {
            conditionnement: {
              '1 bouteille': 'CBO1',
              '2 bouteilles': 'CBO2',
              '3 bouteilles': 'CBO3',
              '6 bouteilles': 'CBO6',
              '12 bouteilles': 'CBO12'
            }
          };

          // Check if variant matches: must match at least one value from EACH metafield key
          let matchesAllMetafields = true;

          for (const [key, allowedValues] of Object.entries(metafieldFilterGroups)) {
            // Find variant's value for this metafield
            const variantMetafieldValue = metafields[key];

            // Map filter values if needed (label → raw value)
            const mappedAllowedValues = allowedValues.map(val => {
              if (labelToValueMap[key] && labelToValueMap[key][val]) {
                return labelToValueMap[key][val];
              }
              return val;
            });

            // Check if variant's value matches ANY allowed value (OR within group)
            const matchesThisMetafield = variantMetafieldValue && mappedAllowedValues.includes(variantMetafieldValue);

            if (!matchesThisMetafield) {
              matchesAllMetafields = false;
              break;  // Fail fast - variant doesn't match this metafield
            }
          }

          // Skip variant only if it fails to match ALL metafield filter groups (AND logic)
          if (!matchesAllMetafields) {
            continue;
          }
        }

        variants.push({
          // Product data
          productId: product.id,
          productTitle: product.title,
          productHandle: product.handle,
          vendor: product.vendor,
          productType: product.productType,
          productImage: product.featuredImage?.url || null,
          productImageAlt: product.featuredImage?.altText || product.title,

          // Variant data
          variantId: variant.id,
          variantTitle: variant.title,
          sku: variant.sku,
          available: variant.availableForSale,
          price: parseFloat(variant.price.amount),
          priceCurrency: variant.price.currencyCode,
          compareAtPrice: variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount) : null,
          variantImage: variant.image?.url || product.featuredImage?.url || null,
          variantImageAlt: variant.image?.altText || product.title,

          // Prefer metafield values (consistent from ERP) over variant options (can be messy)
          size: metafields.contenance ? metafields.contenance + 'cl' : (options.size || options.format || options.taille || variant.title),
          packaging: metafields.conditionnement || options.conditionnement || options.packaging || options.condit || null,
          selectedOptions: variant.selectedOptions,

          // Sur demande flag
          surDemande: metafields.sur_demande === 'true',

          // Collection visibility flag
          visibleInCollection: metafields.visible_in_collection === 'true'
        });
      }
    }

    // Filter by visible_in_collection: if ANY variant in a product has it set,
    // only show those variants. Otherwise show all (backward compatible).
    const productGroups = {};
    for (const v of variants) {
      if (!productGroups[v.productId]) productGroups[v.productId] = [];
      productGroups[v.productId].push(v);
    }

    const filtered = [];
    for (const [productId, productVariants] of Object.entries(productGroups)) {
      const hasVisibilityFlag = productVariants.some(v => v.visibleInCollection);
      if (hasVisibilityFlag) {
        filtered.push(...productVariants.filter(v => v.visibleInCollection));
      } else {
        filtered.push(...productVariants);
      }
    }

    return filtered;
  }

  /**
   * Build ProductFilter array from filter UI state
   * Maps Shopify Liquid filter params to Storefront API filters
   */
  static buildFilters(filterState) {
    const filters = [];

    for (const [paramName, values] of Object.entries(filterState)) {
      if (!values || values.length === 0) continue;

      for (const value of values) {
        if (paramName === 'filter.p.vendor') {
          filters.push({ productVendor: value });
        } else if (paramName.startsWith('filter.v.option.')) {
          // Extract option name from parameter (e.g., filter.v.option.format -> Format)
          const optionKey = paramName.split('.')[3]; // Get the last part (e.g., 'size', 'format')
          // Capitalize first letter to match Shopify's option name format
          const optionName = optionKey.charAt(0).toUpperCase() + optionKey.slice(1);
          filters.push({ variantOption: { name: optionName, value } });
        } else if (paramName.startsWith('filter.p.m.')) {
          // Product metafield filter: filter.p.m.namespace.key
          const parts = paramName.split('.');
          if (parts.length >= 5) {
            filters.push({
              productMetafield: {
                namespace: parts[3],
                key: parts[4],
                value
              }
            });
          }
        } else if (paramName.startsWith('filter.v.m.')) {
          // Variant metafield filter
          const parts = paramName.split('.');
          if (parts.length >= 5) {
            filters.push({
              variantMetafield: {
                namespace: parts[3],
                key: parts[4],
                value
              }
            });
          }
        } else if (paramName === 'filter.p.product_type') {
          filters.push({ productType: value });
        } else if (paramName === 'filter.v.availability') {
          filters.push({ available: value === '1' || value === 'true' });
        }
      }
    }

    return filters;
  }

  /**
   * Parse URL query params into filter state object
   */
  static parseURLFilters(searchParams) {
    const filterState = {};

    for (const [key, value] of searchParams.entries()) {
      if (key.startsWith('filter.')) {
        if (!filterState[key]) {
          filterState[key] = [];
        }
        filterState[key].push(value);
      }
    }

    return filterState;
  }

  /**
   * Build URL query string from filter state
   */
  static buildURLParams(filterState, sortBy = null) {
    const params = new URLSearchParams();

    if (sortBy) {
      params.set('sort_by', sortBy);
    }

    for (const [paramName, values] of Object.entries(filterState)) {
      for (const value of values) {
        params.append(paramName, value);
      }
    }

    return params.toString();
  }
}

// Export for use in other modules
window.StorefrontAPI = StorefrontAPI;
